import type { MultiScenarioPumpChartContext } from "./d";
import type { OperatingPoint } from "@/components/types";
import { getFrequencyRatio } from "@/libs/calculations/pumps/pumpData";
import { getSimplifiedProducedTdh } from "../tornado";
import { convert } from "@/libs/units";
import { getDensityCorrectedProducedPoints } from "@/libs/charting/_common/densityCorrection";

export const getOperatingPoints = (
  context: MultiScenarioPumpChartContext
): OperatingPoint[] => {
  const {
    scenarios,
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitLabel,
    areScenariosEspShore,
    densityCorrectionRatioByScenarioIndex
  } = context;

  const xLabel = `Flow Rate (${targetXUnitLabel})`;
  let yLabel: string;
  if (areScenariosEspShore) {
    yLabel = `Head (${targetYUnitLabel})`;
  } else {
    yLabel = `Diff Pressure (${targetYUnitLabel})`;
  }
  return scenarios
    .reduce<OperatingPoint[]>((points, s, i) => {
      const {
        pumpCalculations,
        ascendingOrderPumps,
        dischargeFluidDensityLBMFT3
      } = s.calcs;
      const parameters = s.parameters;
      const primaryPump = s.calcs.primaryPump.value;
      if (!primaryPump) {
        return points;
      }
      const frequencyRatio = getFrequencyRatio(
        pumpCalculations.aggregate.operatingRPM.value,
        areScenariosEspShore
      );

      const rorMin =
        s.calcs.pumpCalculations.individual.value[primaryPump.id].rorMin;

      const rorMinTotalDynamicHeadData = getSimplifiedProducedTdh(
        rorMin,
        frequencyRatio,
        ascendingOrderPumps.value,
        pumpCalculations.aggregate.gasHeadRerateRatio.value,
        parameters?.pump_wear_head_correction_ratio || 1,
        dischargeFluidDensityLBMFT3.value
      );
      const rorMinTotalDynamicHead = areScenariosEspShore
        ? rorMinTotalDynamicHeadData.tdh
        : rorMinTotalDynamicHeadData.tdhPressure;

      const rorBep =
        s.calcs.pumpCalculations.individual.value[primaryPump.id].rorBep;

      const rorBepTotalDynamicHeadData = getSimplifiedProducedTdh(
        rorBep,
        frequencyRatio,
        ascendingOrderPumps.value,
        pumpCalculations.aggregate.gasHeadRerateRatio.value,
        parameters?.pump_wear_head_correction_ratio || 1,
        dischargeFluidDensityLBMFT3.value
      );

      const rorBepTotalDynamicHead = areScenariosEspShore
        ? rorBepTotalDynamicHeadData.tdh
        : rorBepTotalDynamicHeadData.tdhPressure;

      const rorMax =
        s.calcs.pumpCalculations.individual.value[primaryPump.id].rorMax;

      const rorMaxTotalDynamicHeadData = getSimplifiedProducedTdh(
        rorMax,
        frequencyRatio,
        ascendingOrderPumps.value,
        pumpCalculations.aggregate.gasHeadRerateRatio.value,
        parameters?.pump_wear_head_correction_ratio || 1,
        dischargeFluidDensityLBMFT3.value
      );
      const rorMaxTotalDynamicHead = areScenariosEspShore
        ? rorMaxTotalDynamicHeadData.tdh
        : rorMaxTotalDynamicHeadData.tdhPressure;

      const densityCorrectionRatio = densityCorrectionRatioByScenarioIndex[i];
      const [correctedRorMinPoint, correctedRorBepPoint, correctedRorMaxPoint] =
        getDensityCorrectedProducedPoints(
          [
            { x: rorMin, y: rorMinTotalDynamicHead },
            { x: rorBep, y: rorBepTotalDynamicHead },
            { x: rorMax, y: rorMaxTotalDynamicHead }
          ],
          densityCorrectionRatio
        );

      return points.concat([
        {
          label: "ROR Min",
          color: `var(--plotColor${i})`,
          point: {
            x: Number(correctedRorMinPoint.x),
            y: Number(correctedRorMinPoint.y)
          },
          xLabel,
          yLabel
        },
        {
          label: "ROR Bep",
          color: `var(--plotColor${i})`,
          point: {
            x: Number(correctedRorBepPoint.x),
            y: Number(correctedRorBepPoint.y)
          },
          xLabel,
          yLabel
        },
        {
          label: "ROR Max",
          color: `var(--plotColor${i})`,
          point: {
            x: Number(correctedRorMaxPoint.x),
            y: Number(correctedRorMaxPoint.y)
          },
          xLabel,
          yLabel
        }
      ]);
    }, [])
    .map(operatingPoint => ({
      ...operatingPoint,
      point: {
        x: convert(operatingPoint.point.x, baseXUnit, targetXUnit),
        y: convert(operatingPoint.point.y, baseYUnit, targetYUnit)
      }
    }));
};
